// OnboardingStep1.js
import React, { useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { submitAccessibilityAtom } from "@/store/atoms";
import { useAccessibility, useI18N } from "common/hooks";
import AnalyticsService from "@/services/analytics";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useDarkMode } from "@/hooks/useDarkMode";
import Checkbox from "@/components/common/Checkbox";
import WideButton from "@/components/common/WideButton";
import { XIcon } from "lucide-react";

interface OnboardingStep1Props {
  title: string;
  subtitle: string;
  onClose: () => void;
}

const OnboardingStep1 = ({
  title,
  subtitle,
  onClose,
}: OnboardingStep1Props) => {
  const i18n = useI18N();
  //set index for swipable views
  const { setScreenIndex } = useAuthModals();
  const isDark = useDarkMode();
  //fetch data options to display
  const { accessibilityNeedOptions } = useAccessibility();

  //get and store the data for entire onBoarding steps
  const [submitData, setSubmitData] = useAtom(submitAccessibilityAtom);

  //get and store selected options for this view
  const [selectedOption, setSelectedOption] = useState<string | undefined>(
    submitData?.accessibility_need_selection
  );

  useEffect(() => {
    AnalyticsService.trackGAPagesHandler(
      "authPopupCreateAccountViewedCreateNewAccountAccessNeedsPage"
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountViewedCreateNewAccountAccessNeedsPage",
      {}
    );
  }, []);

  const onContinue = async () => {
    AnalyticsService.trackGAEventHandler(
      "authPopupCreateAccountStep1Complete"
    );
    AnalyticsService.trackMx(
      "authPopupCreateAccountStep1Complete",
      {}
    );

    setSubmitData((prev) => ({
      ...prev,
      accessibility_need_selection: selectedOption,
    }));

    //Move to step 2 for all cases
    setScreenIndex(AuthModalScreen.OnboardingStep2);
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <div
      style={{
        height:
          window.innerHeight < 800
            ? `${window.innerHeight - 40}px`
            : `${window.innerHeight - 100}px`,
      }}
      className="flex flex-col rounded-xl bg-white dark:bg-primary-900 overflow-y-auto"
    >
      <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky top-0">
        <h1
          className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
          tabIndex={0}
          ref={firstFocusRef} // Attach the ref here
        >
          {title}
        </h1>
      </header>
      <div className="px-8 md:px-16 flex-1 flex flex-col">
        <h2
          className="text-primary-900 dark:text-primary-100 desktop-h1-xsmall-extra-bold mt-8"
          tabIndex={0}
        >
          {i18n.get("welcomeToSociability")}
        </h2>
        <p
          className="paragraph-medium-medium text-primary-800 dark:text-primary-150 mt-4"
          tabIndex={0}
        >
          {subtitle}
        </p>
        <hr className="w-full border-primary-150 dark:border-primary-200 my-4" />
        <p
          className="desktop-h2-medium-semi-bold text-primary-900 dark:text-primary-100"
          tabIndex={0}
        >
          {i18n.get("doYouOrSomeoneYouKnowHaveAnyAccessibilityRequirements")}
        </p>
        <span
          className="paragraph-small-semibold !text-primary-500 dark:!text-primary-300 block mt-2"
          tabIndex={0}
        >
          {i18n.get("chooseOne")}
        </span>
        <div className="flex flex-col mt-4">
          {accessibilityNeedOptions.map((option, index) => (
            <div key={index} className="my-2">
              <Checkbox
                checked={selectedOption === option}
                key={index}
                onCheck={(value) => {
                  if (value) {
                    setSelectedOption(option);
                  } else {
                    setSelectedOption(undefined);
                  }
                }}
                label={option}
              />
            </div>
          ))}
        </div>
        <div className="flex-1" />
        <div className="mb-8 items-center justify-center flex flex-col">
          <WideButton
            type="filled"
            onClick={() => onContinue()}
            disabled={!selectedOption}
          >
            {i18n.get("continue")}
          </WideButton>
        </div>
      </div>
      <button
        onClick={onClose}
        className="absolute top-4 left-4"
        aria-label="Close modal"
      >
        <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
      </button>
    </div>
  );
};

export default OnboardingStep1;
